import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";
import {
  DISPOSABLE_DOMAINS,
  MIN_FILL_MS,
  RATE_LIMIT_MAX,
  RATE_LIMIT_WINDOW_MS,
} from "@/lib/waitlist.constants";

import { COMMITMENT_TYPES } from "@/lib/commitment";
import { crewSlug } from "@/lib/crew-card";

const publicRoleColumns = [
  "id",
  "tenant_id",
  "title",
  "slug",
  "summary",
  "description",
  "location",
  "commitment",
  "commitment_types",
  "is_open",
  "sort_order",
  "created_at",
  "updated_at",
] as const;

export interface PublicJobRole {
  id: string;
  tenant_id: string;
  title: string;
  slug: string;
  summary: string;
  description: string;
  location: string;
  commitment: string;
  commitment_types: string[];
  is_open: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export const CREW_STATUSES = [
  "applied",
  "reviewing",
  "interviewing",
  "offered",
  "active",
  "alumni",
  "declined",
  "withdrawn",
] as const;

export type CrewStatus = (typeof CREW_STATUSES)[number];

export interface CrewNote {
  at: string;
  note: string;
  by?: string | null;
}

export interface CrewRow {
  id: string;
  tenant_id: string;
  job_role_id: string | null;
  pilot_id: string | null;
  status: CrewStatus;
  first_name: string;
  last_name: string;
  full_name: string;
  email: string;
  phone: string | null;
  linkedin_url: string | null;
  resume_path: string | null;
  why_squadflight: string;
  anything_else: string;
  slug: string | null;
  title: string | null;
  bio: string | null;
  is_published: boolean;
  notes: CrewNote[];
  ip: string | null;
  user_agent: string | null;
  viewed_at: string | null;
  applied_at: string;
  status_changed_at: string;
  created_at: string;
  updated_at: string;
  job_role?: { title: string; slug: string } | null;
}

/** @deprecated Applications and crewmates now share one row. */
export type JobApplicationRow = CrewRow;

const applicationSchema = z.object({
  jobRoleId: z.string().uuid(),
  firstName: z.string().min(1).max(100),
  lastName: z.string().min(1).max(100),
  email: z.string().email().max(255),
  phone: z.string().max(50).optional().default(""),
  linkedinUrl: z.string().max(500).optional().default(""),
  resumePath: z.string().max(500).optional().default(""),
  whySquadflight: z.string().max(5000).optional().default(""),
  anythingElse: z.string().max(5000).optional().default(""),
  elapsedMs: z.number().int().min(0).default(0),
  company: z.string().max(100).optional().default(""),
});

const roleEditorSchema = z.object({
  id: z.string().uuid().optional(),
  title: z.string().min(1).max(200),
  slug: z
    .string()
    .min(1)
    .max(200)
    .regex(/^[a-z0-9-]+$/),
  summary: z.string().max(1000).default(""),
  description: z.string().max(20000).default(""),
  location: z.string().max(200).default(""),
  commitmentTypes: z.array(z.enum(COMMITMENT_TYPES)).min(1).default(["contract"]),
  isOpen: z.boolean().default(true),
  sortOrder: z.number().int().default(0),
});

function getClientMeta() {
  const headers = getRequest().headers;
  const ip =
    headers.get("cf-connecting-ip") ??
    headers.get("x-forwarded-for")?.split(",")[0]?.trim() ??
    "unknown";
  return { ip, userAgent: headers.get("user-agent") ?? null };
}

async function rateLimitCheck(supabaseAdmin: any, tenantId: string, ip: string) {
  if (ip === "unknown") return;
  const since = new Date(Date.now() - RATE_LIMIT_WINDOW_MS).toISOString();
  const { count } = await supabaseAdmin
    .from("signup_attempts")
    .select("id", { count: "exact", head: true })
    .eq("ip", ip)
    .gte("created_at", since);
  if ((count ?? 0) >= RATE_LIMIT_MAX) {
    throw new Error("Too many submissions from this connection. Try again later.");
  }
  await supabaseAdmin.from("signup_attempts").insert({ ip, tenant_id: tenantId } as never);
}

export const listOpenJobRoles = createServerFn({ method: "GET" }).handler(
  async (): Promise<PublicJobRole[]> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("job_roles")
      .select(publicRoleColumns.join(","))
      .eq("is_open", true)
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: false });
    if (error) throw new Error("Could not load roles.");
    return (data ?? []) as unknown as PublicJobRole[];
  },
);

export const listAllJobRoles = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<PublicJobRole[]> => {
    const { data, error } = await context.supabase
      .from("job_roles")
      .select(publicRoleColumns.join(","))
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: false });
    if (error) throw new Error("Could not load roles.");
    return (data ?? []) as unknown as PublicJobRole[];
  });

export const getJobRoleBySlug = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => {
    const parsed = z.object({ slug: z.string().min(1) }).safeParse(data);
    if (!parsed.success) throw new Error("Invalid role.");
    return parsed.data;
  })
  .handler(async ({ data }): Promise<PublicJobRole | null> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: rows, error } = await supabaseAdmin.rpc("job_role_by_slug", {
      _slug: data.slug,
    });
    if (error) throw new Error("Could not load role.");
    return (rows?.[0] as unknown as PublicJobRole | undefined) ?? null;
  });

export const submitJobApplication = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => {
    const parsed = applicationSchema.safeParse(data);
    if (!parsed.success) {
      throw new Error("Please check your details and try again.");
    }
    return parsed.data;
  })
  .handler(async ({ data }): Promise<{ ok: true }> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { defaultTenantId } = await import("@/lib/tenants.functions");
    const tenantId = await defaultTenantId(supabaseAdmin);
    const { ip, userAgent } = getClientMeta();
    const email = data.email.toLowerCase().trim();

    // Honeypot — say so instead of pretending the application was saved.
    if (data.company.trim().length > 0) {
      throw new Error("We couldn't accept that submission. Please try again.");
    }

    if (data.elapsedMs < MIN_FILL_MS) {
      throw new Error("That submission came in too fast — take another moment and resend.");
    }

    const domain = email.split("@")[1] ?? "";
    if (DISPOSABLE_DOMAINS.has(domain)) {
      throw new Error("Please use a permanent email address.");
    }

    await rateLimitCheck(supabaseAdmin, tenantId, ip);

    // Verify role exists and is open
    const { data: roleRows, error: roleError } = await supabaseAdmin
      .from("job_roles")
      .select("id, title, slug, is_open")
      .eq("id", data.jobRoleId)
      .eq("tenant_id", tenantId)
      .single();
    if (roleError || !roleRows) throw new Error("That role is no longer open.");
    if (!roleRows.is_open) throw new Error("That role is no longer accepting applications.");

    // Deferred identity: match the submitted email against existing pilots.
    // Informational only — never required, never creates a pilot record.
    let pilotId: string | null = null;
    const { data: pilotMatch } = await supabaseAdmin
      .from("pilots")
      .select("id")
      .eq("tenant_id", tenantId)
      .ilike("email", email)
      .is("archived_at", null)
      .order("created_at", { ascending: true })
      .limit(1)
      .maybeSingle();
    if (pilotMatch?.id) pilotId = pilotMatch.id as string;

    const firstName = data.firstName.trim();
    const lastName = data.lastName.trim();
    const payload = {
      tenant_id: tenantId,
      job_role_id: data.jobRoleId,
      pilot_id: pilotId,
      first_name: firstName,
      last_name: lastName,
      full_name: `${firstName} ${lastName}`.trim(),
      email,
      phone: data.phone.trim() || null,
      linkedin_url: data.linkedinUrl.trim() || null,
      resume_path: data.resumePath.trim() || null,
      why_squadflight: data.whySquadflight.trim(),
      anything_else: data.anythingElse.trim(),
      ip: ip === "unknown" ? null : ip,
      user_agent: userAgent,
    };

    // One row per person, from first application through active crew.
    const { data: existingRows } = await supabaseAdmin
      .from("crew")
      .select("id, status")
      .eq("tenant_id", tenantId)
      .ilike("email", email)
      .order("created_at", { ascending: true })
      .limit(1);
    const existing = (existingRows ?? [])[0] as { id: string; status: string } | undefined;

    const selectCols =
      "id, first_name, last_name, email, phone, linkedin_url, resume_path, why_squadflight, anything_else, job_role_id, created_at";

    const { data: saved, error: saveError } = existing
      ? await supabaseAdmin
          .from("crew")
          .update({ ...payload, applied_at: new Date().toISOString() } as never)
          .eq("id", existing.id)
          .select(selectCols)
          .single()
      : await supabaseAdmin
          .from("crew")
          .insert({ ...payload, status: "applied" } as never)
          .select(selectCols)
          .single();

    if (saveError || !saved) {
      console.error("submitJobApplication save failed", saveError);
      throw new Error("Could not save application. Please try again.");
    }
    const inserted = saved as unknown as {
      id: string;
      first_name: string;
      last_name: string;
      email: string;
      phone: string | null;
      linkedin_url: string | null;
      resume_path: string | null;
      why_squadflight: string;
      anything_else: string;
      created_at: string;
    };

    const { sendTemplateEmail } = await import("@/lib/email-templates/send-email");
    try {
      await sendTemplateEmail("job-application-received", "m@squadflight.com", {
        templateData: {
          roleTitle: roleRows.title,
          roleSlug: roleRows.slug,
          firstName: inserted.first_name,
          lastName: inserted.last_name,
          email: inserted.email,
          phone: inserted.phone,
          linkedinUrl: inserted.linkedin_url,
          resumePath: inserted.resume_path,
          whySquadflight: inserted.why_squadflight,
          anythingElse: inserted.anything_else,
          applicationId: inserted.id,
          submittedAt: inserted.created_at,
        },
        replyTo: inserted.email,
      });
    } catch (e) {
      console.error("Failed to send job application alert email:", e);
    }

    return { ok: true };
  });

export const listCrew = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<CrewRow[]> => {
    const { data, error } = await context.supabase
      .from("crew")
      .select("*, job_role:job_role_id(title, slug)")
      .order("created_at", { ascending: false });
    if (error) throw new Error("Could not load the crew list.");
    return (data ?? []) as unknown as CrewRow[];
  });

/** @deprecated Use listCrew — applications and crewmates share one list. */
export const listJobApplications = listCrew;

export const setCrewStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => {
    const parsed = z
      .object({ id: z.string().uuid(), status: z.enum(CREW_STATUSES) })
      .safeParse(data);
    if (!parsed.success) throw new Error("Invalid status change.");
    return parsed.data;
  })
  .handler(async ({ data, context }): Promise<{ ok: true }> => {
    const patch: Record<string, unknown> = { status: data.status };

    // Becoming active earns a public page address. Without a slug the crewmate
    // is invisible on the Crew Profiles page and /crew/<slug> can't resolve.
    if (data.status === "active") {
      const { data: row } = await context.supabase
        .from("crew")
        .select("slug, full_name, first_name, last_name")
        .eq("id", data.id)
        .maybeSingle();

      const existing = row as {
        slug: string | null;
        full_name: string | null;
        first_name: string | null;
        last_name: string | null;
      } | null;

      if (existing && !existing.slug) {
        const source =
          existing.full_name?.trim() ||
          `${existing.first_name ?? ""} ${existing.last_name ?? ""}`.trim();
        const base = crewSlug(source) || "crewmate";

        const { data: taken } = await context.supabase
          .from("crew")
          .select("slug")
          .not("slug", "is", null)
          .like("slug", `${base}%`);

        const used = new Set(
          ((taken ?? []) as Array<{ slug: string | null }>)
            .map((r) => r.slug)
            .filter((s): s is string => !!s),
        );

        let candidate = base;
        let n = 2;
        while (used.has(candidate)) candidate = `${base}-${n++}`;
        patch["slug"] = candidate;
      }
    }

    const { error } = await context.supabase
      .from("crew")
      .update(patch as never)
      .eq("id", data.id);
    if (error) {
      console.error("setCrewStatus failed", error);
      throw new Error("Could not update that status.");
    }
    return { ok: true };
  });

export const addCrewNote = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => {
    const parsed = z
      .object({ id: z.string().uuid(), note: z.string().trim().min(1).max(2000) })
      .safeParse(data);
    if (!parsed.success) throw new Error("Write a note first.");
    return parsed.data;
  })
  .handler(async ({ data, context }): Promise<{ notes: CrewNote[] }> => {
    const { data: row, error: readError } = await context.supabase
      .from("crew")
      .select("notes")
      .eq("id", data.id)
      .single();
    if (readError || !row) throw new Error("Could not find that person.");

    const rawNotes = (row as { notes?: unknown }).notes;
    const existing: CrewNote[] = Array.isArray(rawNotes) ? (rawNotes as unknown as CrewNote[]) : [];
    const notes: CrewNote[] = [
      ...existing,
      {
        at: new Date().toISOString(),
        note: data.note.trim(),
        by: (context.claims?.email as string | undefined) ?? null,
      },
    ];

    const { error } = await context.supabase
      .from("crew")
      .update({ notes } as never)
      .eq("id", data.id);
    if (error) {
      console.error("addCrewNote failed", error);
      throw new Error("Could not save that note.");
    }
    return { notes };
  });

export const saveJobRole = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => {
    const parsed = roleEditorSchema.safeParse(data);
    if (!parsed.success) throw new Error("Please check the role details.");
    return parsed.data;
  })
  .handler(async ({ data, context }): Promise<{ id: string; slug: string }> => {
    const { defaultTenantId } = await import("@/lib/tenants.functions");
    const tenantId = await defaultTenantId(context.supabase);
    const { data: role, error } = await context.supabase
      .from("job_roles")
      .upsert(
        {
          id: data.id,
          tenant_id: tenantId,
          title: data.title,
          slug: data.slug,
          summary: data.summary,
          description: data.description,
          location: data.location,
          commitment: data.commitmentTypes[0] ?? "contract",
          commitment_types: data.commitmentTypes,
          is_open: data.isOpen,
          sort_order: data.sortOrder,
        } as never,
        { onConflict: "id" },
      )
      .select("id, slug")
      .single();
    if (error || !role) {
      console.error("saveJobRole error:", error);
      throw new Error("Could not save role.");
    }
    return { id: role.id as string, slug: role.slug as string };
  });

export const deleteJobRole = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => {
    const parsed = z.object({ id: z.string().uuid() }).safeParse(data);
    if (!parsed.success) throw new Error("Invalid role.");
    return parsed.data;
  })
  .handler(async ({ data, context }): Promise<{ ok: true }> => {
    const { error } = await context.supabase.from("job_roles").delete().eq("id", data.id);
    if (error) throw new Error("Could not delete role.");
    return { ok: true };
  });

export const getResumeDownloadUrl = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => {
    const parsed = z.object({ path: z.string().min(1) }).safeParse(data);
    if (!parsed.success) throw new Error("Invalid resume.");
    return parsed.data;
  })
  .handler(async ({ data, context }): Promise<{ signedUrl: string }> => {
    const { data: signed, error } = await context.supabase.storage
      .from("resumes")
      .createSignedUrl(data.path, 60 * 60); // 1 hour
    if (error || !signed?.signedUrl) throw new Error("Could not create resume link.");
    return { signedUrl: signed.signedUrl };
  });
